import { Box, useTheme } from "@mui/material"
import { forwardRef, useEffect, useMemo, useRef, useState } from "react"
import { prevent } from "lib/prevent"
import { alpha } from "@mui/material/styles"
import { raiseOnce } from "library/local-events/index.mjs"
import { ChangeView, GetSelectable } from "event-definitions"
import { generate } from "library/guid/index.mjs"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { cleanObject } from "lib/clean-object"

export const Selectable = forwardRef(function Selectable(
    { children, onSelected, group = "default", event = "SelectableChanged", ...props },
    ref
) {
    const selectedRef = useRef(false)
    const theme = useTheme()
    const [id] = useState(generate)
    const { allSelected = { a: 1 } } = useBoundContext()

    const [isSelected, setSelected] = useState(false)
    selectedRef.current = isSelected

    useEffect(
        () => () => {
            setSelected(false)
            cleanObject(allSelected)
            ChangeView.raiseOnce()
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    )

    useMemo(() => {
        allSelected[id] = isSelected
    }, [isSelected, allSelected, id])
    GetSelectable.useEvent(retrieveSelectable)

    return (
        <Box
            {...props}
            ref={ref}
            onClick={prevent(toggle)}
            sx={{
                cursor: "pointer",
                "&:hover": {
                    background: isSelected
                        ? alpha(theme.palette.primary.light, 0.5)
                        : alpha(theme.palette.primary.light, 0.3),
                    outline: isSelected
                        ? `3px solid ${alpha(theme.palette.primary.light, 0.9)}`
                        : `3px dotted ${alpha(theme.palette.primary.light, 0.5)}`,
                },
                outlineOffset: 2,
                "& *": {
                    userSelect: "none",
                },
                borderRadius: 1,
                mb: 1,
                mr: 0.5,
                background: isSelected
                    ? alpha(theme.palette.primary.light, 0.1)
                    : alpha(theme.palette.primary.light, 0.0),
                outline: isSelected ? `3px solid ${alpha(theme.palette.primary.light, 0.9)}` : undefined,
            }}
        >
            {children}
        </Box>
    )

    function toggle() {
        setSelected(!isSelected)
        raiseOnce(event)
    }

    function retrieveSelectable({ selected = {} }) {
        if (selectedRef.current) {
            const list = (selected[group] = selected[group] ?? [])
            list.push(onSelected)
        }
    }
})
